import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInWithRedirect, signOut } from "aws-amplify/auth";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/base/button";
import { useAdminApp } from "@/hooks/useAdminApp";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { ROUTES } from "@/routes";

export function AdminLoginView() {
  const [searchParams] = useSearchParams();
  const accessDenied = searchParams.get("error") === "access_denied";
  const navigate = useNavigate();
  const { adminUserBloc } = useAdminApp();
  const [state] = useStateMachineValue(adminUserBloc);
  const [isSigningIn, setIsSigningIn] = useState(false);

  // On mount, ask the bloc to resolve the current session. If we discover
  // the user is already an authenticated admin, bounce them to the dashboard
  // rather than showing the sign-in button (which would throw).
  useEffect(() => {
    adminUserBloc.checkAuth();
  }, [adminUserBloc]);

  useEffect(() => {
    if (state.status === "authenticated") {
      navigate(ROUTES.SIGNALS, { replace: true });
    }
  }, [state.status, navigate]);

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      // Start clean: clear any lingering Cognito session before initiating
      // OAuth. This prevents UserAlreadyAuthenticatedException when the user
      // lands here with a stale token (e.g. the access_denied signOut hadn't
      // fully propagated, or they manually navigated to /login).
      await signOut();
      await signInWithRedirect({ provider: "Google" });
    } finally {
      // signInWithRedirect navigates away on success, so this usually won't
      // run — but it re-enables the button if the redirect itself fails.
      setIsSigningIn(false);
    }
  };

  // While we're still resolving the current session, don't flash the login UI.
  if (state.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center">
      <div className="mx-auto w-full max-w-sm space-y-6 px-4">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold tracking-tight">CPOS Admin</h1>
          <p className="text-muted-foreground text-sm">
            Internal administration portal
          </p>
        </div>

        {accessDenied && (
          <div className="border-destructive/50 bg-destructive/10 flex items-start gap-3 rounded-lg border p-4">
            <ShieldAlert className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-1">
              <p className="text-destructive text-sm font-medium">
                Access denied
              </p>
              <p className="text-muted-foreground text-sm">
                CPOS Admin group membership is required. Contact your
                administrator if you believe this is an error.
              </p>
            </div>
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          disabled={isSigningIn}
          onClick={handleSignIn}
        >
          <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </Button>

        <p className="text-muted-foreground text-center text-xs">
          Restricted to authorized CPOS administrators only.
        </p>
      </div>
    </div>
  );
}
